import { Button, ButtonProps } from "@/lib/components/Button/Button";
import { cn, formatPrice } from "@/lib/utils";
import {
  CreateCheckoutSessionDto,
  SubscriptionTierType,
} from "@repo/api/billing";
import {
  calculateImages,
  getBaseCreditsForPrice,
  getSubscriptionTierConfig,
} from "@repo/api/utils/billing";
import { useCreateCheckoutSession } from "@/lib/queries/billing-queries";
import { CouponDto } from "@repo/api/coupon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import {
  CouponBenefit,
  getCouponBenefitForPlanTier,
} from "@repo/api/utils/coupon";
import { IconCheck, IconGift } from "@tabler/icons-react";

export type PlanType = "SUBSCRIPTION" | "ONE_OFF";
export type PlanOptionBadgeType =
  | "MOST_POPULAR"
  | "FOR_THIS_VIDEO"
  | "CURRENT_SUBSCRIPTION";

export type PlanTierProps = {
  type: PlanType;
  price: number;
  credits: number;
  currency: string;
  badge?: PlanOptionBadgeType;
  subscriptionTier?: SubscriptionTierType;
  activeSubscriptionTier?: SubscriptionTierType;
  onCreateCheckoutSuccess?: () => void;
  teamExternalId: string;
  redirectVideoExternalId?: string;
  currentCoupon: CouponDto | null;
};

export default function PlanTier({
  type,
  price,
  credits,
  currency,
  badge,
  subscriptionTier,
  activeSubscriptionTier,
  onCreateCheckoutSuccess,
  teamExternalId,
  redirectVideoExternalId,
  currentCoupon,
}: PlanTierProps) {
  const badgeText = formatBadge({ badge });
  const couponBenefit = getCouponBenefitForPlanTier({
    planType: type,
    coupon: currentCoupon,
    credits,
  });

  return (
    <div
      className={cn(
        "relative shrink-0 grow-0 basis-auto overflow-hidden rounded-xl border bg-[#2F2F2F] p-6 text-white",
      )}
    >
      {badgeText ? <RibbonBadge text={badgeText} badge={badge} /> : null}
      <img
        src="/CreditIconOutlineFaded.svg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -bottom-2 h-full w-full opacity-15 select-none"
      />
      <div className="relative z-10">
        <p className="text-[28px] leading-tight font-bold">
          {`${formatPrice(price, currency)}`}
          {type === "SUBSCRIPTION" && (
            <span className="ml-2 text-sm font-medium text-white">/mo</span>
          )}
        </p>
        <CreditsText
          planType={type}
          couponBenefit={couponBenefit}
          credits={credits}
        />
        <PurchaseButton
          type={type}
          subscriptionTier={subscriptionTier}
          activeSubscriptionTier={activeSubscriptionTier}
          credits={credits}
          teamExternalId={teamExternalId}
          onCreateCheckoutSuccess={onCreateCheckoutSuccess}
          redirectVideoExternalId={redirectVideoExternalId}
          currentCoupon={currentCoupon}
          badge={badge}
        />
        <div className="mt-6 flex flex-col gap-3">
          <SubscriptionOneOffComparisonBenefitRow
            planType={type}
            credits={credits}
            price={price}
          />
          <AnimateImagesBenefitRow badge={badge} credits={credits} />
          <SubscriptionExtraCreditsBenefitRow
            type={type}
            currentCoupon={currentCoupon}
            couponBenefit={couponBenefit}
          />
        </div>
      </div>
    </div>
  );
}

function RibbonBadge({
  text,
  badge,
}: {
  text: string;
  badge?: PlanOptionBadgeType;
}) {
  if (!badge) {
    return null;
  }

  const isGradient = badge === "MOST_POPULAR";

  return (
    <div className="absolute -top-[1px] -right-[1px] z-20 h-28 w-28">
      <div
        className={cn(
          "absolute top-[26px] right-[-46px] w-[170px] rotate-45 py-1.5 text-center text-xs font-semibold shadow-sm",
          isGradient
            ? "bg-linear-to-br from-primary-400 to-secondary-400 text-white"
            : "bg-white",
        )}
      >
        {isGradient ? (
          text
        ) : (
          <span className="bg-linear-to-br from-primary-400 to-secondary-400 bg-clip-text text-transparent">
            {text}
          </span>
        )}
      </div>
    </div>
  );
}

function BenefitRow({
  benefit,
  icon,
}: {
  benefit: string | React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex h-4.25 w-4.25 items-center justify-center">
        {icon ?? <IconCheck size={20} />}
      </div>

      <p className="text-sm leading-tight">{benefit}</p>
    </div>
  );
}

function SubscriptionOneOffComparisonBenefitRow({
  planType,
  credits,
  price,
}: {
  planType: PlanType;
  credits: number;
  price: number;
}) {
  if (planType !== "SUBSCRIPTION") {
    return null;
  }

  const baseCredits = getBaseCreditsForPrice(price);
  const extraCredits = credits - baseCredits;
  const extraPercentage = Math.floor((extraCredits / baseCredits) * 100);

  const tooltipTriggerText = `+${extraPercentage}% credits `;
  const tooltipText = `You get ${extraCredits.toLocaleString("en-US")} extra credits compared to one-time purchase`;

  return (
    <BenefitRow
      benefit={
        <>
          <Tooltip delayDuration={0}>
            <TooltipTrigger className="font-medium underline decoration-dashed decoration-[0px] underline-offset-3">
              {tooltipTriggerText}
            </TooltipTrigger>
            <TooltipContent>{tooltipText}</TooltipContent>
          </Tooltip>{" "}
          vs one-time
        </>
      }
    />
  );
}

function AnimateImagesBenefitRow({
  credits,
  badge,
}: {
  credits: number;
  badge: PlanOptionBadgeType | undefined;
}) {
  const calculatedImages = calculateImages(credits);

  let tooltipTriggerText = `${calculatedImages.toLocaleString("en-US")} ${calculatedImages === 1 ? "image" : "images"}`;

  if (badge === "FOR_THIS_VIDEO") {
    tooltipTriggerText = "this video";
  }

  let tooltipText = `Example: You can create ${Math.floor(calculatedImages / 8)} videos with 8 images`;

  if (badge === "FOR_THIS_VIDEO") {
    tooltipText =
      "Enough credits for animating the current images in the video";
  }

  return (
    <BenefitRow
      benefit={
        <>
          Animate{" "}
          <Tooltip delayDuration={0}>
            <TooltipTrigger className="font-medium underline decoration-dashed decoration-[0px] underline-offset-3">
              {tooltipTriggerText}
            </TooltipTrigger>
            <TooltipContent>{tooltipText}</TooltipContent>
          </Tooltip>
        </>
      }
    />
  );
}

function SubscriptionExtraCreditsBenefitRow({
  type,
  currentCoupon,
  couponBenefit,
}: {
  type: PlanType;
  currentCoupon: CouponDto | null;
  couponBenefit: CouponBenefit | null;
}) {
  if (
    type !== "SUBSCRIPTION" ||
    !couponBenefit ||
    couponBenefit.type !== "EXTRA_CREDITS" ||
    !currentCoupon
  ) {
    return null;
  }

  let tooltipText = "Coupon benefit";

  if (currentCoupon.config.campaignTitle) {
    tooltipText = `Benefit from ${currentCoupon.config.campaignTitle}`;
  }

  return (
    <div className="flex items-start gap-2">
      <div className="flex h-4.25 w-4.25 items-center justify-center">
        <IconGift className="size-4" />
      </div>
      <p className="text-sm leading-tight">
        <Tooltip delayDuration={0}>
          <TooltipTrigger>
            <span
              className="font-medium underline decoration-dashed decoration-[0px] underline-offset-3"
              style={{
                textDecorationStyle: "dashed",
                textDecorationSkipInk: "none",
              }}
            >
              {`+${couponBenefit.extraCredits.toLocaleString("en-US")} extra credits`}
            </span>
          </TooltipTrigger>
          <TooltipContent>{tooltipText}</TooltipContent>
        </Tooltip>{" "}
        in first month
      </p>
    </div>
  );
}

function formatBadge({ badge }: { badge?: PlanOptionBadgeType }) {
  switch (badge) {
    case "MOST_POPULAR":
      return "Most Popular";
    case "FOR_THIS_VIDEO":
      return "This Video";
    case "CURRENT_SUBSCRIPTION":
      return "Current sub";
    default:
      return null;
  }
}

function PurchaseButton({
  type,
  subscriptionTier,
  activeSubscriptionTier,
  credits,
  teamExternalId,
  onCreateCheckoutSuccess,
  redirectVideoExternalId,
  currentCoupon,
  badge,
}: {
  type: PlanType;
  subscriptionTier?: SubscriptionTierType;
  activeSubscriptionTier?: SubscriptionTierType;
  credits: number;
  teamExternalId: string;
  onCreateCheckoutSuccess?: () => void;
  redirectVideoExternalId?: string;
  currentCoupon: CouponDto | null;
  badge?: PlanOptionBadgeType;
}) {
  const { mutate: createCheckoutSession, isPending } = useCreateCheckoutSession(
    {
      teamExternalId,
      onSuccess: (data) => {
        onCreateCheckoutSuccess?.();
        window.location.href = data.url;
      },
    },
  );

  const data: CreateCheckoutSessionDto =
    type === "SUBSCRIPTION"
      ? {
          type: "SUBSCRIPTION",
          tier: subscriptionTier!,
          redirectVideoExternalId: redirectVideoExternalId,
          couponCode: currentCoupon?.code,
        }
      : {
          type: "ONE_OFF",
          credits: credits,
          redirectVideoExternalId: redirectVideoExternalId,
          couponCode: currentCoupon?.code,
        };

  let label = "Purchase";
  let variant: ButtonProps["variant"] =
    badge === "MOST_POPULAR" ? "default" : "outline";

  if (type === "SUBSCRIPTION") {
    label = "Subscribe";
    variant = badge === "MOST_POPULAR" ? "default" : "outline";

    if (activeSubscriptionTier) {
      const activeSubscriptionPrice = getSubscriptionTierConfig(
        activeSubscriptionTier,
      ).price;
      const subscriptionPrice = getSubscriptionTierConfig(
        subscriptionTier!,
      ).price;

      if (activeSubscriptionTier === subscriptionTier) {
        label = "Renew now";
        variant = "outline";
      } else if (activeSubscriptionPrice < subscriptionPrice) {
        label = "Upgrade";
        variant = "default";
      } else {
        label = "Downgrade";
        variant = "outline";
      }
    }
  }

  return (
    <Button
      className="w-full"
      size="sm"
      variant={variant}
      onClick={() => createCheckoutSession(data)}
      disabled={isPending}
      isLoading={isPending}
    >
      {label}
    </Button>
  );
}

type CreditsTextProps = {
  planType: PlanType;
  couponBenefit: CouponBenefit | null;
  credits: number;
};

function CreditsText({ planType, couponBenefit, credits }: CreditsTextProps) {
  const showCouponCredits =
    couponBenefit?.type === "EXTRA_CREDITS" && planType === "ONE_OFF";

  return (
    <div className="mt-0.25 mb-5 flex items-center gap-1.5">
      <img
        src="/CreditIcon.svg"
        alt=""
        aria-hidden="true"
        className="h-4 w-4 shrink-0"
      />
      <p className="text-sm">
        {showCouponCredits ? (
          <>
            <span className="text-xs text-white/50 line-through">
              {credits.toLocaleString("en-US")}
            </span>{" "}
          </>
        ) : null}
        <span className="font-medium text-white">
          {(showCouponCredits
            ? credits + couponBenefit.extraCredits
            : credits
          ).toLocaleString("en-US")}{" "}
          credits
          {planType === "SUBSCRIPTION" && "/mo"}
        </span>
      </p>
    </div>
  );
}
